import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link2, Loader2, Search } from 'lucide-react';

import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { resolveSlugs } from '@/services/api/price-structure';
import { useToast } from '@/hooks/use-toast';
import type {
    BulkSimulateRow, EngineVersion, LinearEngineParams, SegmentDetail, TypeSource,
} from '@/types/price-structure';

import {
    DEFAULT_LINEAR_PARAMS, HEALTH_LABEL, HEALTH_STYLE, OUTCOME_LABEL, OUTCOME_STYLE,
    evaluateHealth,
} from '../constants';
import type { Health, HealthFactor } from '../constants';
import { explainLinear, explainSegment } from '../explain';
import { GranulationTree, type TreeBlock } from './GranulationTree';

interface SimulationDetailModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    domainId: number | null;
    domainName: string;
    typeSource: TypeSource;
    aggressiveness: number;
    granularity: number;
    engineVersion?: EngineVersion;
    linearParams?: LinearEngineParams | null;
    result: BulkSimulateRow | null;
}


/**
 * Com type_source = utm_campaign o custom_value é o nome da campanha, não um id
 * de post do WordPress: não há slug para resolver nem página para abrir.
 */
const isPostSource = (source: TypeSource) => source === 'id_post_wp';

export function SimulationDetailModal({
    open, onOpenChange, domainId, domainName, typeSource, aggressiveness, granularity,
    engineVersion = 'v3', linearParams = null, result,
}: SimulationDetailModalProps) {
    const byPost = isPostSource(typeSource);
    const unitLabel = byPost ? 'post' : 'campanha';
    const { toast } = useToast();
    const [search, setSearch] = useState('');
    const [slugs, setSlugs] = useState<Record<string, string | null>>({});

    // Cada abertura do modal começa limpa — estado de outro domínio confunde.
    useEffect(() => {
        if (open) {
            setSearch('');
            setSlugs({});
        }
    }, [open, result?.domain_id]);

    const segments = result?.segments ?? [];
    const needle = search.trim().toLowerCase();

    const items = useMemo<TreeBlock<SegmentDetail>[]>(() => {
        const filtered = needle
            ? segments.filter(
                  (s) =>
                      s.custom_value.toLowerCase().includes(needle) ||
                      s.country.toLowerCase().includes(needle) ||
                      s.block.toLowerCase().includes(needle) ||
                      (slugs[s.custom_value] ?? '').toLowerCase().includes(needle),
              )
            : segments;

        return filtered.map((s) => ({
            country: s.country,
            post: s.custom_value,
            block: s.block,
            steps: s.steps ?? [],
            impressions: s.impressions,
            extra: s,
        }));
    }, [segments, needle, slugs]);

    const countryCount = useMemo(
        () => new Set(items.map((i) => i.country)).size,
        [items],
    );

    const postIds = useMemo(
        () => [...new Set(segments.map((s) => s.custom_value))],
        [segments],
    );

    const slugMutation = useMutation({
        mutationFn: () => resolveSlugs(domainId as number, postIds),
        onSuccess: (data) => {
            setSlugs(data.slugs);
            const found = Object.values(data.slugs).filter(Boolean).length;
            toast({
                title: 'URLs tratadas',
                description: `${found} de ${postIds.length} post(s) com slug encontrado no WordPress.`,
                variant: found === 0 ? 'destructive' : undefined,
            });
        },
        onError: (error: any) =>
            toast({
                variant: 'destructive',
                title: 'Erro ao tratar URLs',
                description: error.response?.data?.detail || 'Falha ao consultar o WordPress.',
            }),
    });

    if (!result) return null;

    const verdict = evaluateHealth(
        result.avg_steps_delivered,
        result.steps_requested,
        result.segments_found,
        result.segments_skipped,
    );
    const health = verdict.level;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex flex-wrap items-center gap-2">
                        {domainName}
                        <span
                            className={`rounded-full border px-2 py-0.5 text-xs font-medium ${HEALTH_STYLE[health]}`}
                        >
                            {HEALTH_LABEL[health]}
                        </span>
                        {byPost && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="ml-auto"
                                disabled={!domainId || slugMutation.isPending}
                                onClick={() => slugMutation.mutate()}
                            >
                                {slugMutation.isPending ? (
                                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Link2 className="mr-2 h-3.5 w-3.5" />
                                )}
                                Tratar URLs
                            </Button>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        {result.window && (
                            <>Janela {result.window.start} a {result.window.end} · </>
                        )}
                        {result.segments_found} blocos em {countryCount} país
                        {countryCount === 1 ? '' : 'es'} · média {result.avg_steps_delivered} de{' '}
                        {result.steps_requested} steps
                        {result.segments_skipped > 0 && (
                            <span className="text-red-600">
                                {' '}· {result.segments_skipped} sem granulação
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <Diagnosis level={health} factors={verdict.factors} summary={verdict.summary} />

                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={`Filtrar por país, ${unitLabel}${byPost ? ', slug' : ''} ou bloco...`}
                            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <GranulationTree<SegmentDetail>
                        items={items}
                        forceExpanded={needle !== ''}
                        byPost={byPost}
                        unitLabel={unitLabel}
                        domainName={domainName}
                        slugs={slugs}
                        emptyMessage={
                            needle
                                ? 'Nenhum bloco corresponde ao filtro.'
                                : 'Nenhum bloco nesta simulação.'
                        }
                        renderBlockMeta={(item) => <BlockMeta segment={item.extra as SegmentDetail} />}
                        renderBlockDetail={(item) => (
                            <BlockExplanation
                                segment={item.extra as SegmentDetail}
                                aggressiveness={aggressiveness}
                                granularity={granularity}
                                engineVersion={engineVersion}
                                linearParams={linearParams}
                            />
                        )}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}

/** Explica a nota: qual eixo puxou para baixo e contra qual limiar. */
function Diagnosis({
    level, factors, summary,
}: {
    level: Health;
    factors: HealthFactor[];
    summary: string;
}) {
    if (!factors.length) {
        return (
            <div className={`rounded-lg border px-3 py-2 text-xs ${HEALTH_STYLE[level]}`}>
                {summary}
            </div>
        );
    }

    const dot: Record<Health, string> = {
        good: 'bg-emerald-500',
        warn: 'bg-amber-500',
        bad: 'bg-red-500',
    };

    return (
        <div className={`rounded-lg border px-3 py-2.5 ${HEALTH_STYLE[level]}`}>
            <p className="text-xs font-medium">
                Por que “{HEALTH_LABEL[level]}”: {summary}
            </p>
            <ul className="mt-1.5 space-y-1">
                {factors.map((factor) => (
                    <li key={factor.label} className="flex items-start gap-2 text-[11px]">
                        <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${dot[factor.level]}`} />
                        <span className="text-gray-700">
                            <strong className="font-medium">{factor.label}:</strong> {factor.detail}
                        </span>
                    </li>
                ))}
            </ul>
            <p className="mt-1.5 text-[11px] text-gray-600">
                A nota é a pior dos dois eixos. Segmentos sem granulação aparecem marcados na
                árvore abaixo, com o motivo.
            </p>
        </div>
    );
}

/** Métricas ao lado do nome do bloco. */
function BlockMeta({ segment }: { segment: SegmentDetail }) {
    const isSkipped = !segment.steps?.length;
    return (
        <>
            {isSkipped && (
                <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${OUTCOME_STYLE[segment.outcome]}`}
                >
                    {OUTCOME_LABEL[segment.outcome]}
                </span>
            )}
            <span className="text-[11px] text-gray-400">
                eCPM {segment.ecpm?.toFixed(2)} · PMR {segment.pmr?.toFixed(2)}%
            </span>
        </>
    );
}

/** Veredito, aviso e a aritmética sob demanda. */
function BlockExplanation({
    segment, aggressiveness, granularity, engineVersion, linearParams,
}: {
    segment: SegmentDetail;
    aggressiveness: number;
    granularity: number;
    engineVersion: EngineVersion;
    linearParams: LinearEngineParams | null;
}) {
    const [showMath, setShowMath] = useState(false);
    const explanation =
        engineVersion === 'v4'
            ? explainLinear(segment, linearParams ?? DEFAULT_LINEAR_PARAMS)
            : explainSegment(segment, aggressiveness, granularity);
    const isSkipped = !segment.steps?.length;

    return (
        <>
            <p
                className={`mt-2 rounded border-l-2 px-2 py-1.5 text-[11px] leading-relaxed ${
                    isSkipped
                        ? 'border-red-300 bg-red-50/60 text-gray-700'
                        : 'border-blue-300 bg-blue-50/50 text-gray-700'
                }`}
            >
                {explanation.verdict}
            </p>

            {explanation.caveat && (
                <p
                    className={`mt-1 text-[11px] ${
                        segment.degradation_reason?.startsWith('piso estendido')
                            ? 'rounded border-l-2 border-amber-400 bg-amber-50/70 px-2 py-1 text-gray-700'
                            : 'text-amber-700'
                    }`}
                >
                    {explanation.caveat}
                </p>
            )}

            {!isSkipped && (
                <>
                    <button
                        type="button"
                        onClick={() => setShowMath((v) => !v)}
                        className="mt-1.5 text-[11px] font-medium text-blue-600 hover:underline"
                    >
                        {showMath ? 'Ocultar a conta' : 'Ver a conta'}
                    </button>

                    {showMath && (
                        <table className="mt-1.5 w-full text-[11px]">
                            <tbody className="divide-y divide-gray-100">
                                {explanation.lines.map((line) => (
                                    <tr key={line.label}>
                                        <td className="py-1 pr-3 align-top font-medium text-gray-600 whitespace-nowrap">
                                            {line.label}
                                        </td>
                                        <td className="py-1 pr-3 align-top font-mono text-gray-500">
                                            {line.formula}
                                        </td>
                                        <td className="py-1 text-right align-top font-mono font-medium text-gray-800 whitespace-nowrap">
                                            {line.result}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}
        </>
    );
}
